import React from "react";
import _ from "lodash";
import {connect} from "react-redux";

import withStyles from "@material-ui/core/styles/withStyles";
import Grid from "@material-ui/core/Grid";
import FormLabel from "@material-ui/core/FormLabel";

import PrimaryButton from "../../../../../../../components/Buttons/PrimaryButton";
import {isRequiredQuestion, setQuestionErrorFromConfig} from "../../../../../utils";
import {default as RefAccountForm} from "./components/RefAccountForm";
import {QuestionnairesHandlerResource} from "../../../../../../../utils/api";
import {CUSTOMER_TYPES} from "../../../../../constants";
import BaseDialog from "../../../../../../../components/BaseDialog/BaseDialog";
import {displaySuccessSnackBar} from "../../../../../../../components/SnackbarProvider/actions";
import {bankAccountToOption, isValidBankAccount} from "./ReferenceAccountsQuestion";
import RadioCard from "../../formElement/RadioCard";
import DefaultBankAccountLogo from "../../../../../../../images/RiskProfiling/default-account-logo.svg";

import styles from "./stylesV2";

const findBankLogo = (bankLogosConfig, account) => {
  const IBAN = account.iban || '';
  let bank;
  if(IBAN.slice(0, 2) === 'DE'){
    const BLZ = IBAN.slice(4, 12);
    if(BLZ) {
      bank = _.find(bankLogosConfig, (b) => b.bank_german_code && (
        b.bank_german_code === BLZ || (b.bank_german_code.length < BLZ.length && _.endsWith(BLZ, b.bank_german_code))
      ));
    }
  }

  if(!bank && account.bic){
    // we should sort banks with longer BIC first
    const sortedByBIC = _.orderBy(bankLogosConfig, [(b) => (b.bic || '').length], ['desc']);
    bank = _.find(sortedByBIC, (b) => b.bic && _.startsWith(account.bic, b.bic))
  }

  if(!bank && account.name){
    bank = _.find(bankLogosConfig, (b) => b.name === account.name || (
      b.key_words && _.some(b.key_words, (key) => account.name.indexOf(key) !== -1))
    )
  }

  return _.get(bank, 'logo') || DefaultBankAccountLogo;
};

const RefAccountCard = ({account, bankLogosConfig}) => (
  <Grid container spacing={3} alignItems={"center"} style={{textAlign: 'left'}}>
    <Grid item xs={'auto'} style={{width: 124}}>
      <img src={findBankLogo(bankLogosConfig, account)} alt={""} style={{maxWidth: '100%'}} />
    </Grid>
    <Grid item xs>
      <Grid container>
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={'auto'} style={{minWidth: 110}}><b>Kreditinstitut:</b></Grid>
            <Grid item xs>{account.name}</Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={'auto'} style={{minWidth: 110}}><b>Kontoinhaber:</b></Grid>
            <Grid item xs>{account.accountHolder}</Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={'auto'} style={{minWidth: 110}}><b>IBAN:</b></Grid>
            <Grid item xs>{account.iban}</Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={'auto'} style={{minWidth: 110}}><b>BIC:</b></Grid>
            <Grid item xs>{account.bic}</Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  </Grid>
);

const ReferenceAccountsQuestionV2 = props => {
  const {
    classes,
    dispatch,
    question,
    onAnswerChange,
    dataService,
  } = props;

  const [openDialog, setOpenDialog] = React.useState(false);
  const [formDataLoading, setFormDataLoading] = React.useState(false);
  const [formData, setFormData] = React.useState(null);
  const [formDataErrors, setFormDataErrors] = React.useState({});

  const bankAccounts = React.useMemo(() => (question.bank_accounts || []).map(account => (
    {
      ...bankAccountToOption(account),
      label: <RefAccountCard account={account} bankLogosConfig={dataService.refAccountBanksLogos} />
    }
  )), [question.bank_accounts]);

  React.useEffect(() => {
    if(question.answer) {
      // check/update answer according to existing bank accounts
      const acc = bankAccounts.find(acc => acc.value === question.answer.value);
      // in case account not in the list - clear answer
      if(!acc){
        question.error = null;  // clean error
        onAnswerChange(question.uid, null);
      }
    }
  }, []);

  React.useEffect(() => {
    if (!question.answer && bankAccounts.length > 0) {
      question.error = null;  // clean error
      onAnswerChange(question.uid, bankAccounts[0]);
    }
  });

  const handleChange = (value) => {
    const acc = bankAccounts.find(acc => acc.value === value);
    question.error = null;  // clean error
    onAnswerChange(question.uid, {...acc, label: undefined});  // we should not store option label
  };

  const handleAddClick = () => {
    setOpenDialog(true);
  };

  const handleCreateAccount = async () => {
    // validate account holder is minor or guardians
    if(dataService.customer_type === CUSTOMER_TYPES.MINOR){
      const names = [
        dataService.getMemberFullName(undefined,', ', true),
        ...dataService.members.map((m, idx) => dataService.getMemberFullName(idx, ', ', true))
      ];
      if(!names.includes(formData.accountHolder)){
        setFormDataErrors({
          ...formDataErrors,
          accountHolder: 'Sie können nur ein Konto verwenden, über welches entweder der Minderjährige oder einer der Erziehungsberechtigten verfügt.'
        });
        return;
      }
    }

    setFormDataLoading(true);
    QuestionnairesHandlerResource.at(`customer-info/${dataService.customer_id}/`).post({
      bankAccounts: [formData]
    }).then(res => {
      const account = {...formData};
      account.value = account.id = account.iban;
      account.label = [account.iban, account.name, account.accountHolder].join(' / ');

      question.bank_accounts = [...question.bank_accounts, account];
      dispatch(displaySuccessSnackBar('Das neue Referenzkonto wurde erfolgreich hinzugefügt.'));
      handleModalClose();
    }).catch(errors => {
      setQuestionErrorFromConfig(question, errors.data || errors, (q) => {
        return {dante_field: 'bankAccounts[0]'}
      });
      setFormDataErrors(errors.data || errors);
    }).finally(() => setFormDataLoading(false));
  };

  const handleModalClose = () => {
    setOpenDialog(false);
    setFormDataErrors({});
    setFormData(null);
  };

  return (
    <Grid container spacing={2} className={classes.questionContainer}>
      {bankAccounts.length === 0 && (
        <Grid item xs={12}>
          <FormLabel className={classes.noAccountsLabel} error={!!question.error}>
            Bitte fügen Sie ein Referenzkonto hinzu.
          </FormLabel>
        </Grid>
      )}
      <Grid item xs={12}>
        <PrimaryButton
          text={'Neues Referenzkonto hinzufügen'}
          icon={<i className={'far fa-plus'} />}
          onButtonClick={handleAddClick}
          variant={'text'}
          disabled={question.disabled}
        />
      </Grid>
      {bankAccounts.length > 0 && (
        <Grid item xs={12}>
          <RadioCard
            value={question.answer && question.answer.value || ''}
            required={isRequiredQuestion(question)}
            disabled={question.disabled}
            options={bankAccounts}
            onChange={handleChange}
            custom_classes={question.custom_classes || {}}
            error={question.error}
            row={question.row}
            newDesign
          />
        </Grid>
      )}
      <BaseDialog
        open={!!openDialog}
        handleClose={handleModalClose}
        maxWidth={'sm'}
        title={'Neues Referenzkonto hinzufügen'}
        actions={
          <Grid container spacing={1}>
            <Grid item>
              <span className={classes.stepRequired}>*</span> Pflichtfeld
            </Grid>
            <Grid item style={{marginLeft: 'auto'}}>
              <PrimaryButton
                text={'Abbrechen'}
                onButtonClick={handleModalClose}
                variant={'outlined'}
              />
            </Grid>
            <Grid item>
              <PrimaryButton
                text={'Speichern'}
                disabled={formDataLoading || !isValidBankAccount(formData, true)}
                onButtonClick={handleCreateAccount}
              />
            </Grid>
          </Grid>
        }
      >
        <RefAccountForm
          handleFormChange={setFormData}
          errors={formDataErrors}
          setErrors={setFormDataErrors}
          newDesign
        />
      </BaseDialog>
    </Grid>
  );
};

export default connect()(withStyles(styles)(ReferenceAccountsQuestionV2));